import React, { useRef, useEffect, useState } from 'react';
import ReviewFormComponent from './common/ReviewFormComponent';
import { COMPONENT_TYPES } from '../constants';

// 모듈화된 컴포넌트 시스템과 어댑터 임포트
import { getModularizedComponent } from './module';
import { createComponentProps } from './module/adapters/componentAdapters';

// 미리보기 모드에서 사용할 컴포넌트 렌더러
// Redux에 의존하지 않고 props로 받은 데이터만 사용
function ComponentRendererWithProps({ component, allComponents, pathVariables = {} }) {
  const componentRef = useRef(null);
  const [deviceType, setDeviceType] = useState('pc'); // 기본값은 PC

  // 디바이스 타입 감지 및 창 크기 변경 감지
  useEffect(() => {
    // 디바이스 타입 계산 함수
    const calculateDeviceType = () => {
      // window 객체가 존재하는지 확인 (SSR 대비)
      if (typeof window !== 'undefined') {
        const isMobile = window.innerWidth <= 990; // 991px부터는 PC로 설정
        return isMobile ? 'mobile' : 'pc'; // 모바일/태블릿은 'mobile'로 통합
      }
      return 'pc'; // 기본값
    };

    // 초기 디바이스 타입 설정
    setDeviceType(calculateDeviceType());

    // 창 크기 변경 이벤트 리스너
    const handleResize = () => {
      setDeviceType(calculateDeviceType());
    };

    // 이벤트 리스너 등록
    window.addEventListener('resize', handleResize);

    // 클린업 함수
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
  }, [component.id, component.type]);
  
  // 디바이스 타입 변경 이벤트 리스너 추가
  useEffect(() => {
    // 디바이스 타입 변경 이벤트 리스너
    const handleDeviceTypeChange = (event) => {
      const { deviceType: newDeviceType } = event.detail;
      if (newDeviceType && newDeviceType !== deviceType) {
        // 디바이스 타입 변경
        setDeviceType(newDeviceType);
      }
    };
    
    // 이벤트 리스너 등록
    window.addEventListener('deviceTypeChanged', handleDeviceTypeChange);
    
    // 클린업 함수
    return () => {
      window.removeEventListener('deviceTypeChanged', handleDeviceTypeChange);
    };
  }, [deviceType]);
  
  if (!component) {
    console.error('컴포넌트 데이터가 없습니다.');
    return <div className="error-component">잘못된 컴포넌트 데이터</div>;
  }
  
  // 이 컴포넌트의 자식 컴포넌트들 찾기
  const childComponents = allComponents.filter(comp => comp.parentId === component.id);
  
  
  // 부모 컴포넌트 찾기
  const parentComponent = component.parentId ? allComponents.find(comp => comp.id === component.parentId) : null;
  
  // 컴포넌트 타입을 정규화하고 확인
  let componentType = component.type;
  if (typeof componentType === 'string') {
    componentType = componentType.toUpperCase();
  }
  
  const isChildOfColumn = parentComponent && 
    (parentComponent.type === COMPONENT_TYPES.COLUMN || 
     (typeof parentComponent.type === 'string' && parentComponent.type.toUpperCase() === 'COLUMN'));
  
  const isChildOfRow = parentComponent && 
    (parentComponent.type === COMPONENT_TYPES.ROW || 
     (typeof parentComponent.type === 'string' && parentComponent.type.toUpperCase() === 'ROW'));
  
  // 오류 객체를 문자열로 변환하는 함수
  const errorToString = (err) => {
    if (!err) return '';
    if (typeof err === 'string') return err;
    if (typeof err === 'object') {
      return err.message || JSON.stringify(err);
    }
    return String(err);
  };

  const renderComponent = () => {
    try {
      // 타입 정규화
      const typeStr = String(component.type).toUpperCase();
      
      // 1. 어댑터를 사용하여 컴포넌트 Props 생성
      const props = createComponentProps({
        ...component,
        pathVariables // 추가 필요한 데이터 전달
      }, deviceType);
      
      // 미리보기를 위한 추가 props
      const previewProps = {
        ...props,
        isPreview: true,
        viewMode: deviceType
      };
      
      // 2. 모듈화된 컴포넌트 가져오기
      const ModularizedComponent = getModularizedComponent(typeStr);
      
      // 특별한 경우: REVIEW_FORM 컴포넌트는 아직 모듈화되지 않았으므로 기존 컴포넌트 사용
      if (typeStr === 'REVIEW_FORM' || typeStr === COMPONENT_TYPES.REVIEW_FORM) {
        return <ReviewFormComponent {...previewProps} />;
      }
      
      // 특별한 경우: TAB_LAYOUT 컴포넌트는 아직 모듈화되지 않았으므로 기존 컴포넌트 사용
      if (typeStr === 'TAB_LAYOUT' || typeStr === COMPONENT_TYPES.TAB_LAYOUT) {
        return <div>TabLayout 컴포넌트는 아직 모듈화되지 않았습니다.</div>;
      }
      
      // 3. 모듈화된 컴포넌트가 없으면 오류 표시
      if (!ModularizedComponent) {
        console.error(`모듈화된 컴포넌트를 찾을 수 없습니다: ${typeStr}`);
        return (
          <div className="unknown-component" style={{ 
            padding: '20px', 
            border: '1px dashed #aaa',
            borderRadius: '4px',
            color: '#666',
            backgroundColor: '#f8f8f8',
            textAlign: 'center',
            ...previewProps.style
          }}>
            알 수 없는 컴포넌트 타입: {typeStr}
          </div>
        );
      }
      
      // 4. Container 등의 레이아웃 컴포넌트인 경우 자식 처리
      if (typeStr === 'CONTAINER' || typeStr === COMPONENT_TYPES.CONTAINER) {
        return (
          <ModularizedComponent {...previewProps}>
            {childComponents.length > 0 ? 
              childComponents.map(child => (
                <ComponentRendererWithProps 
                  key={child.id} 
                  component={child} 
                  allComponents={allComponents}
                  pathVariables={pathVariables}
                />
              )) 
              : previewProps.content
            }
          </ModularizedComponent>
        );
      }
      
      // 5. 일반 컴포넌트 렌더링
      return <ModularizedComponent {...previewProps} />;
    } catch (error) {
      console.error('컴포넌트 렌더링 중 오류:', error, component);
      // 에러 메시지를 안전하게 문자열로 변환
      const errorMessage = errorToString(error);
      
      return (
        <div className="component-error">
          <p>컴포넌트 렌더링 오류</p>
          <p>{errorMessage}</p>
          <pre>{JSON.stringify(component, null, 2)}</pre>
        </div>
      );
    }
  };

  // 컴포넌트의 클래스 이름 설정 - 미리보기 모드 스타일 적용
  const componentClassName = `component-wrapper preview-mode component-type-${String(component.type).toLowerCase()}`;
  
  // 자동 높이 사용 여부 - 이제 모든 컴포넌트가 모듈화되어 자동 높이 사용
  const useAutoHeight = true;
  
  // IMAGE_TEXT 컴포넌트는 minHeight 제한 없이 콘텐츠에 맞게 자동 조정
  const isImageTextComponent = component.type === COMPONENT_TYPES.IMAGE_TEXT || component.type === 'IMAGE_TEXT';
  
  // 기본 스타일 설정 (컴포넌트 데이터가 불완전한 경우에도 동작)
  const componentStyle = {
    position: 'relative',
    width: component.size?.width || '100%',
    height: useAutoHeight ? 'auto' : (component.size?.height || 'auto'),
    minHeight: useAutoHeight ? (isImageTextComponent ? 'unset' : '150px') : undefined,
    boxSizing: 'border-box',
    paddingTop: component.style?.paddingTop || '',
    paddingRight: component.style?.paddingRight || '',
    paddingBottom: component.style?.paddingBottom || '',
    paddingLeft: component.style?.paddingLeft || '',
    ...(component.style?.deviceProperty && component.style.deviceProperty[deviceType] ? {
      paddingTop: component.style.deviceProperty[deviceType].paddingTop || component.style.paddingTop || '',
      paddingRight: component.style.deviceProperty[deviceType].paddingRight || component.style.paddingRight || '',
      paddingBottom: component.style.deviceProperty[deviceType].paddingBottom || component.style.paddingBottom || '',
      paddingLeft: component.style.deviceProperty[deviceType].paddingLeft || component.style.paddingLeft || '',
    } : {})
  };
  
  // LOGIN 컴포넌트인 경우에만 padding을 0으로 강제 설정
  if (String(component.type).toUpperCase() === 'LOGIN') {
    componentStyle.padding = 0;
    componentStyle.paddingTop = 0;
    componentStyle.paddingRight = 0;
    componentStyle.paddingBottom = 0;
    componentStyle.paddingLeft = 0;
  }

  return (
    <div
      className={componentClassName}
      style={componentStyle}
      data-component-id={component.id}
      data-component-type={component.type}
    >
      <div ref={componentRef} className="component-content">
        {renderComponent()}
      </div>
    </div>
  );
}

export default ComponentRendererWithProps; 